import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@components/Header/Header';
import MenuSidebar from '@components/MenuSidebar/MenuSidebar';
import getMenuItems from '@const/menuItems';
import { getAllOrders } from '@services/orderService';
import { ScrollArea, ScrollBar } from '@/ui/scroll-area';
import { useAuth } from '@hooks/useAuth';
import OptimizedLottie from '../../components/LazyLottie/OptimizedLottie';
import emptyBoxAnimation from '@assets/emptybox.json';
// Removed AuthGuard import to allow guest access
import { logger } from '../../utils/logger';

interface OrderItem {
  productId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  code: string;
  subtotal: number;
}

interface Order {
  _id: string;
  orderNumber: string;
  userId: string;
  items: OrderItem[];
  totalAmount: number;
  totalItems: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  payment: {
    method: string;
    status: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
    amount: number;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export default function OrderHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const itemsPerPage = 10;
  const [showFilter, setShowFilter] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = selectedStatus === 'all' ? undefined : selectedStatus;
      const response = await getAllOrders(currentPage, itemsPerPage, statusParam);
      if (response.success && response.data) {
        // Handle the API response structure
        const ordersData = Array.isArray(response.data) ? response.data : [];
        const metaData = response.meta || {};

        setFilteredOrders(ordersData); // Set filtered orders directly from API response
        setTotalOrders('total' in metaData ? Number(metaData.total) : ordersData.length);
        setTotalPages(
          'pages' in metaData
            ? Number(metaData.pages)
            : Math.ceil(
                ('total' in metaData ? Number(metaData.total) : ordersData.length) / itemsPerPage,
              ),
        );
      } else {
        setFilteredOrders([]);
        setTotalOrders(0);
        setTotalPages(1);
      }
    } catch (error) {
      logger.error('Failed to fetch orders:', error);
      setFilteredOrders([]);
      setTotalOrders(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, selectedStatus]);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, isAuthenticated, fetchOrders]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showFilter && !target.closest('.filter-dropdown')) {
        setShowFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter]);

  const handleFilterChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1); // Reset to first page when filtering
    setShowFilter(false); // Close filter dropdown
  };

  const clearFilter = () => {
    setSelectedStatus('all');
    setShowFilter(false);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div
        className="overflow-x-hidden bg-[#04BFBF]/20"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))`,
          backgroundSize: 'cover',
          backgroundPosition: 'left',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      >
        <Header />
        <div className="w-full min-h-dvh flex flex-col">
          <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar */}
                <MenuSidebar
                  title={t('my_account')}
                  currentPage="orders"
                  menuItems={getMenuItems(t)}
                />

                {/* Main Content */}
                <div className="flex-1">
                  <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#04BFBF]"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-x-hidden bg-[#04BFBF]/20"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))`,
        backgroundSize: 'cover',
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <Header />
      <div className="w-full min-h-dvh flex flex-col">
        <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Sidebar */}
              <MenuSidebar
                title={t('my_account')}
                currentPage="orders"
                menuItems={getMenuItems(t)}
              />

              {/* Main Content */}
              <div className="flex-1">
                <div className="bg-white rounded-xl overflow-hidden">
                  {/* Header - Sticky */}
                  <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-6 pb-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-gray-900">{t('orderHistory.title')}</h2>
                      <div className="relative filter-dropdown">
                        <button
                          onClick={() => setShowFilter(!showFilter)}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors rounded-lg ${
                            selectedStatus !== 'all'
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                            />
                          </svg>
                          {t('orderHistory.filter')}
                          {selectedStatus !== 'all' && (
                            <span className="ml-1 bg-blue-500 text-white text-xs rounded-full w-2 h-2"></span>
                          )}
                        </button>

                        {/* Filter Dropdown */}
                        {showFilter && (
                          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-gray-900">
                                  {t('orderHistory.filterByStatus')}
                                </h3>
                                {selectedStatus !== 'all' && (
                                  <button
                                    onClick={clearFilter}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    {t('common.clear')}
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2">
                                {[
                                  { value: 'all', label: t('common.all') },
                                  { value: 'pending', label: t('orderHistory.status.pending') },
                                  {
                                    value: 'processing',
                                    label: t('orderHistory.status.processing'),
                                  },
                                  { value: 'completed', label: t('orderHistory.status.completed') },
                                  { value: 'failed', label: t('orderHistory.status.failed') },
                                  { value: 'cancelled', label: t('orderHistory.status.cancelled') },
                                ].map((status) => (
                                  <button
                                    key={status.value}
                                    onClick={() => handleFilterChange(status.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                      selectedStatus === status.value
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  >
                                    {status.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table Header - Sticky - Hidden on mobile */}
                  <div className="hidden md:block sticky top-[88px] bg-gray-100 mx-6 rounded-lg p-3">
                    <div className="grid grid-cols-6 gap-4 text-sm font-medium text-gray-700">
                      <div className="col-span-2">{t('orderHistory.orderId')}</div>
                      <div>{t('orderHistory.date')}</div>
                      <div>{t('orderHistory.totalItem')}</div>
                      <div>{t('orderHistory.totalPrice')}</div>
                      <div className="text-right">{t('orderHistory.orderStatus')}</div>
                    </div>
                  </div>

                  {/* Scrollable Orders List */}
                  <ScrollArea className="md:h-[calc(100dvh-320px)] w-full md:pt-6">
                    <div className="p-4 md:p-6 pt-4 md:pt-2">
                      <div className="space-y-3">
                        {!Array.isArray(filteredOrders) || filteredOrders.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <div className="relative z-20 flex-1 flex items-center justify-center">
                              <OptimizedLottie
                                animationData={emptyBoxAnimation}
                                className="w-3/4 h-48 mx-auto"
                                loop={true}
                                autoplay={true}
                                lazy={false}
                                priority="high"
                              />
                            </div>
                            {selectedStatus === 'all'
                              ? t('orderHistory.noOrders')
                              : t('orderHistory.noOrdersForFilter')}
                          </div>
                        ) : (
                          filteredOrders.map((order) => (
                            <div
                              key={order._id}
                              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => navigate(`/orders/${order._id}`)}
                            >
                              {/* Desktop Layout */}
                              <div className="hidden md:grid grid-cols-6 gap-4 items-center text-sm">
                                <div className="col-span-2 font-medium text-gray-900">
                                  #{order.orderNumber}
                                </div>
                                <div className="text-gray-600">{formatDate(order.createdAt)}</div>
                                <div className="text-gray-600">
                                  {order.totalItems ||
                                    order.items.reduce((total, item) => total + item.quantity, 0)}
                                </div>
                                <div className="font-medium text-gray-900">
                                  {order.totalAmount.toLocaleString()} MMK
                                </div>
                                <div className="text-right">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}
                                  >
                                    {t(`orderHistory.status.${order.status}`)}
                                  </span>
                                </div>
                              </div>

                              {/* Mobile Layout */}
                              <div className="md:hidden space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="font-medium text-gray-900 text-sm">
                                    #{order.orderNumber}
                                  </div>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}
                                  >
                                    {t(`orderHistory.status.${order.status}`)}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <div className="text-gray-500 text-xs mb-1">
                                      {t('orderHistory.date')}
                                    </div>
                                    <div className="text-gray-900">
                                      {formatDate(order.createdAt)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 text-xs mb-1">
                                      {t('orderHistory.totalItem')}
                                    </div>
                                    <div className="text-gray-900">
                                      {order.totalItems ||
                                        order.items.reduce(
                                          (total, item) => total + item.quantity,
                                          0,
                                        )}
                                    </div>
                                  </div>
                                </div>
                                <div className="pt-2 border-t border-gray-100">
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-500 text-xs">
                                      {t('orderHistory.totalPrice')}
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      {order.totalAmount.toLocaleString()} MMK
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <ScrollBar orientation="vertical" />
                  </ScrollArea>

                  {/* Pagination - Sticky at bottom */}
                  {totalPages > 1 && (
                    <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 p-4 md:p-6 pt-4">
                      {/* Desktop Layout */}
                      <div className="hidden md:flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          {t('orderHistory.showing')} {(currentPage - 1) * itemsPerPage + 1} -{' '}
                          {Math.min(currentPage * itemsPerPage, totalOrders)} {t('orderHistory.of')}{' '}
                          {totalOrders} {t('orderHistory.orders')}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('orderHistory.previous')}
                          </button>

                          {/* Page numbers */}
                          <div className="flex items-center space-x-1">
                            {/* First page */}
                            {currentPage > 3 && totalPages > 5 && (
                              <>
                                <button
                                  onClick={() => setCurrentPage(1)}
                                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  1
                                </button>
                                <span className="px-2 text-gray-500">...</span>
                              </>
                            )}

                            {/* Page range */}
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                                    currentPage === pageNum
                                      ? 'bg-blue-500 text-white border-blue-500'
                                      : 'border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}

                            {/* Last page */}
                            {currentPage < totalPages - 2 && totalPages > 5 && (
                              <>
                                <span className="px-2 text-gray-500">...</span>
                                <button
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  {totalPages}
                                </button>
                              </>
                            )}
                          </div>

                          <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('orderHistory.next')}
                          </button>
                        </div>
                      </div>

                      {/* Mobile Layout */}
                      <div className="md:hidden space-y-3">
                        {/* Page info */}
                        <div className="text-center text-xs text-gray-600">
                          {t('orderHistory.showing')} {(currentPage - 1) * itemsPerPage + 1} -{' '}
                          {Math.min(currentPage * itemsPerPage, totalOrders)} {t('orderHistory.of')}{' '}
                          {totalOrders} {t('orderHistory.orders')}
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-1 mr-2"
                          >
                            {t('orderHistory.previous')}
                          </button>

                          {/* Current page indicator */}
                          <div className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md font-medium">
                            {currentPage} / {totalPages}
                          </div>

                          <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-1 ml-2"
                          >
                            {t('orderHistory.next')}
                          </button>
                        </div>

                        {/* Page numbers for mobile - improved */}
                        <div className="flex items-center justify-center space-x-1 overflow-x-auto px-4">
                          {/* Show first page if not in range */}
                          {currentPage > 2 && totalPages > 3 && (
                            <>
                              <button
                                onClick={() => setCurrentPage(1)}
                                className="px-2 py-1 text-xs border border-gray-300 rounded-md min-w-[32px] hover:bg-gray-50"
                              >
                                1
                              </button>
                              {currentPage > 3 && (
                                <span className="text-gray-400 text-xs">...</span>
                              )}
                            </>
                          )}

                          {/* Current page range */}
                          {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage === 1) {
                              pageNum = i + 1;
                            } else if (currentPage === totalPages) {
                              pageNum = totalPages - 2 + i;
                            } else {
                              pageNum = currentPage - 1 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-2 py-1 text-xs border rounded-md min-w-[32px] transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : 'border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          {/* Show last page if not in range */}
                          {currentPage < totalPages - 1 && totalPages > 3 && (
                            <>
                              {currentPage < totalPages - 2 && (
                                <span className="text-gray-400 text-xs">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(totalPages)}
                                className="px-2 py-1 text-xs border border-gray-300 rounded-md min-w-[32px] hover:bg-gray-50"
                              >
                                {totalPages}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
